/// <amd-module name="Test/TestCaseResults/Redux/TestCaseResultsSaga" />
import { effects, SagaIterator } from "redux-saga";
import { ActionsOfType } from "redux/Utils/ActionHelper";
import * as Constants from "Test/TestCaseResults/Constants";
import { TelemetryActions } from "Test/TestCaseResults/Redux/TelemetryActions";
import { TestCaseResultsActions, TestCaseResultsActionsTypes } from "Test/TestCaseResults/Redux/TestCaseResultsActions";
import { getDataProviderContentForTestResults, getDisplayName } from "Test/TestCaseResults/Utils";
import * as Contracts from "TestPlanCommon/Contracts/TestPlan";
import { ITfsPageService } from "Tfs/Platform/Page";
import { format } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssLocationService } from "VSS/Platform/Location";
import { ContentViewObserverActions, ContentViewObserverActionTypes, getCurrentFilterState, isFilterActive, isFilterVisible } from "VSS/Redux/Modules/ContentViewObserver";
import { IFilterDetails } from "VSS/Redux/Modules/ContentViewObserver/ContentViewObserverContracts";
import { IFilterState } from "VSSUI/Utilities/Filter";
import { TestCaseResultsBreadCrumbService, TestCaseResultsBreadCrumbServiceName } from "../Services/TestCaseResultsBreadCrumbService";
import { IRunDetailsItem } from "./TestCaseResultsContracts";
import { filterResults } from "./TestCaseResultsFilterHelper";
import { getResultItemsFromState, isAnyFilterValueSet } from "./TestCaseResultsSelector";
import { errorHandledSaga } from "Test/TestPlansHubRefresh/TestHub/Util";

export function* TestCaseResultsSaga(): SagaIterator {
    yield effects.takeLatest(TestCaseResultsActionsTypes.loadInitialResultsData, _loadTestData);
    yield effects.takeLatest(TestCaseResultsActionsTypes.openRunsHub, _openRunsHub);
    yield effects.debounce(100, ContentViewObserverActionTypes.FilterStateChanged, _evaluateFilter);
    yield effects.takeLatest(TestCaseResultsActionsTypes.triggerEvaluateFilter, _evaluateFilter);
    yield effects.takeLatest(ContentViewObserverActionTypes.FilterToggled, _filterToggled);
}

export function* loadTestData(action: ActionsOfType<TestCaseResultsActions, TestCaseResultsActionsTypes.loadInitialResultsData>): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;

    const pageService = (yield effects.call([pageContext, pageContext.getService], "ITfsPageService")) as any;
    const pageData = (yield effects.call([pageService, pageService.getData])) as any;

    if (!pageData || !pageData.project) {
        throw new Error("Project missing");
    }

    const data = getDataProviderContentForTestResults(pageContext);

    // Set breadcrumb
    const breadcrumbService = pageContext.getService<TestCaseResultsBreadCrumbService>(TestCaseResultsBreadCrumbServiceName);
    if (data.contextPoint) {
        const projectName = pageData.project.name;

        const locationService: IVssLocationService = (yield effects.call([pageContext, pageContext.getService], "IVssLocationService")) as any;
        const routeValues: { [key: string]: string } = {
            project: projectName
        };
        let hubRefreshUrl = (yield effects.call([locationService, locationService.routeUrl], Constants.TestHubRefreshRouteId, routeValues)) as any;
        hubRefreshUrl += format(Constants.TestHubRefreshQueryParams, data.contextPoint.plan.id);
        breadcrumbService.addBreadCrumb("ContextTestPlan", data.contextPoint.plan.name, hubRefreshUrl, 100);
    }

    breadcrumbService.addBreadCrumb("TestCaseName", data.testCaseName, "", 101);

    // Set data
    yield effects.put(TestCaseResultsActions.loadInitialResultsDataSucceeded(data));

    // set filters
    const filterDetails = getInitialFilterDetails(pageData, data);
    yield effects.put(ContentViewObserverActions.initializeFilterState(filterDetails));

    // Telemetry
    const telemetryPayload = {
        resultsCount: data.results.length,
        testCaseName: data.testCaseName,
        pointId: data.contextPoint ? data.contextPoint.pointId : undefined
    };
    yield effects.put(TelemetryActions.publishTelemetry(Constants.LOAD_INITIAL_RESULTS, telemetryPayload));
}

export function* openRunsHub(action: ActionsOfType<TestCaseResultsActions, TestCaseResultsActionsTypes.openRunsHub>): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;

    const tfsPageService: ITfsPageService = (yield effects.call([pageContext, pageContext.getService], "ITfsPageService")) as any;
    const tfsPageData = (yield effects.call([tfsPageService, tfsPageService.getData])) as any;
    let projectName = "";
    if (tfsPageData && tfsPageData.project) {
        projectName = tfsPageData.project.name;
    }

    const locationService: IVssLocationService = (yield effects.call([pageContext, pageContext.getService], "IVssLocationService")) as any;
    const routeValues: { [key: string]: string } = {
        project: projectName
    };
    // constructing new plan result using contribution id
    let resultUrl = (yield effects.call([locationService, locationService.routeUrl], Constants.TestRunsPageRouteId, routeValues)) as any;
    resultUrl += format(Constants.testRunsPageQueryParams, action.payload.runId, action.payload.resultId);

    const newWindow = (yield effects.call([window, window.open], resultUrl, "_blank")) as any;
    if (newWindow) {
        newWindow.opener = null;
    }

    const telemetryPayload = {
        pointId: action.payload.pointId,
        runId: action.payload.runId,
        resultId: action.payload.resultId
    };
    yield effects.put(TelemetryActions.publishTelemetry(Constants.OPEN_RUNS_HUB, telemetryPayload));
}

/**
 * Runs when the filter state changes, figures out which results match the filter and updates filtered results in store
 */
export function* evaluateFilter(action: ActionsOfType<ContentViewObserverActions, ContentViewObserverActionTypes.FilterStateChanged>): SagaIterator {
    const filterActiveOrValueSet: boolean = (yield effects.call(isFilterActiveOrFilterValueSet)) as any;

    if (filterActiveOrValueSet) {
        const allResultItems: Contracts.TestCaseAssociatedResult[] = (yield effects.select(getResultItemsFromState)) as any;

        const filteredResultIds: IRunDetailsItem[] = (yield effects.call(filterResults, allResultItems)) as any;
        yield effects.put(TestCaseResultsActions.updateFilteredResults(filteredResultIds));
    } else {
        yield effects.put(TestCaseResultsActions.updateFilteredResults(undefined));
    }
}

/**
 * Runs whenever the filter state is toggled (enabled/disabled)
 */
export function* filterToggled(action: ActionsOfType<ContentViewObserverActions, ContentViewObserverActionTypes.FilterStateChanged>): SagaIterator {
    const filterActiveOrValueSet: boolean = (yield effects.call(isFilterActiveOrFilterValueSet)) as any;

    if (filterActiveOrValueSet) {
        yield effects.put(TestCaseResultsActions.triggerEvaluateFilter());
    }

    const telemetryPayload = {
        isFilterVisible: yield effects.select(isFilterVisible),
        isFilterActive: filterActiveOrValueSet
    };
    yield effects.put(TelemetryActions.publishTelemetry(Constants.FILTER_TOGGLED, telemetryPayload));
}

/**
 * Returns true if either the filter is active or if any of the filter value is set
 * Filter active = filter is visible AND any of the filter value is set
 * We need this method because we want to evaluate filter even if it is hidden but a value in it is set.
 */
export function* isFilterActiveOrFilterValueSet(): SagaIterator {
    const filterActive: boolean = (yield effects.select(isFilterActive)) as any;

    const filterState: IFilterState | undefined = (yield effects.select(getCurrentFilterState)) as any;
    const isFilterValueSet: boolean = (yield effects.call(isAnyFilterValueSet, filterState)) as any;

    return filterActive || isFilterValueSet;
}

function getInitialFilterDetails(pageData: any, data: Contracts.TestCaseResultsData): IFilterDetails {
    const projectId = pageData.project.id;
    const filterDetails: IFilterDetails = {
        projectId,
        filterState: {},
        filterVisible: false,
        filterKey: "testcase-results-filter-key"
    };

    // If the context point is set, set initial filter states
    if (data.contextPoint) {
        filterDetails.filterVisible = true;
        filterDetails.filterState[Constants.ConfigurationFilterKey] = {
            value: [data.contextPoint.configuration.name]
        };
        filterDetails.filterState[Constants.TesterFilterKey] = {
            value: [getDisplayName(data.contextPoint.tester)]
        };
        filterDetails.filterState[Constants.PlanFilterKey] = {
            value: [data.contextPoint.plan.name]
        };
        filterDetails.filterState[Constants.SuiteFilterKey] = {
            value: [data.contextPoint.suite.name]
        };
    }

    return filterDetails;
}

const _loadTestData = errorHandledSaga(function* (
    action: ActionsOfType<TestCaseResultsActions, TestCaseResultsActionsTypes.loadInitialResultsData>
): SagaIterator {
    yield effects.call(loadTestData, action);
});

const _openRunsHub = errorHandledSaga(function* (
    action: ActionsOfType<TestCaseResultsActions, TestCaseResultsActionsTypes.openRunsHub>
): SagaIterator {
    yield effects.call(openRunsHub, action);
});

const _evaluateFilter = errorHandledSaga(function* (
    action: ActionsOfType<ContentViewObserverActions, ContentViewObserverActionTypes.FilterStateChanged>
): SagaIterator {
    yield effects.call(evaluateFilter, action);
});

const _filterToggled = errorHandledSaga(function* (
    action: ActionsOfType<ContentViewObserverActions, ContentViewObserverActionTypes.FilterStateChanged>
): SagaIterator {
    yield effects.call(filterToggled, action);
});